/** Fullscreen lightbox with swipe, keyboard nav, and slideshow autoplay. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import { motion, AnimatePresence } from 'framer-motion';
import {
  IconX,
  IconChevronLeft,
  IconChevronRight,
  IconPlayerPlayFilled,
  IconPlayerPauseFilled,
  IconShare,
  IconDownload,
  IconInfoCircle,
} from '@tabler/icons-react';
import { Media } from './Media.tsx';
import { useSlideshow } from '../../hooks/useSlideshow.ts';
import { cn } from '../../lib/cn.ts';
import type { MediaItem } from '../../lib/types.ts';
import type { ExifData } from '../../lib/types.ts';

interface MediaViewerProps {
  items: MediaItem[];
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  onShare?: (item: MediaItem) => void;
  onDownload?: (item: MediaItem) => void;
  /** EXIF lookup for the info panel (by original key). */
  exifFor?: (key: string) => ExifData | undefined;
  /** Load more items when nearing the end (infinite gallery). */
  onNearEnd?: () => void;
}

const CONTROLS_HIDE_MS = 2800;

export function MediaViewer({
  items,
  index,
  onIndexChange,
  onClose,
  onShare,
  onDownload,
  exifFor,
  onNearEnd,
}: MediaViewerProps) {
  const [showControls, setShowControls] = useState(true);
  const [showInfo, setShowInfo] = useState(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const indexRef = useRef(index);
  indexRef.current = index;

  const item = items[index];

  const goTo = useCallback(
    (next: number) => {
      const clamped = Math.max(0, Math.min(items.length - 1, next));
      onIndexChange(clamped);
      if (clamped >= items.length - 3) onNearEnd?.();
    },
    [items.length, onIndexChange, onNearEnd],
  );

  const prev = useCallback(() => goTo(indexRef.current - 1), [goTo]);
  const next = useCallback(() => goTo(indexRef.current + 1), [goTo]);

  const slideshow = useSlideshow({
    count: items.length,
    onAdvance: goTo,
    getIndex: () => indexRef.current,
    loop: true,
  });

  // Keyboard navigation.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'Escape') onClose();
      else if (e.key === ' ') {
        e.preventDefault();
        slideshow.toggle();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [prev, next, onClose, slideshow]);

  // Auto-hide controls after inactivity.
  const bumpControls = useCallback(() => {
    setShowControls(true);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setShowControls(false), CONTROLS_HIDE_MS);
  }, []);

  useEffect(() => {
    bumpControls();
    return () => {
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, [bumpControls, index]);

  const swipe = useSwipeable({
    onSwipedLeft: next,
    onSwipedRight: prev,
    onSwipedDown: onClose,
    // Higher delta = only fires on a decisive swipe, so a short accidental drag
    // doesn't page forward. Also ignores iOS's edge-back gesture better.
    delta: 40,
    // Don't fake touches from mouse — on iOS it caused ghost drags after a tap.
    trackMouse: false,
    // Tell the browser we handle horizontal gestures ourselves; vertical stays
    // native (so tap-to-close-controls + scroll still work).
    preventScrollOnSwipe: true,
  });

  if (!item) return null;
  const exif = exifFor?.(item.key);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex h-[100dvh] items-center justify-center bg-black"
      onMouseMove={bumpControls}
      role="dialog"
      aria-modal="true"
      aria-label={`Viewing ${item.displayName}`}
    >
      {/* Ambient glow: the current photo blown up + blurred behind everything,
          tinting the backdrop with the photo's own colors. Desktop only —
          scale+blur+saturate on a full-viewport image is very expensive on iOS
          Safari (drops the whole viewer to <30fps on older iPhones). */}
      {item.kind === 'image' && (
        <AnimatePresence mode="wait">
          <motion.div
            key={`ambient-${item.key}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="pointer-events-none absolute inset-0 hidden overflow-hidden sm:block"
          >
            <Media
              fileKey={item.key}
              kind="image"
              alt=""
              priority
              className="h-full w-full scale-125 blur-3xl saturate-150"
            />
            <div className="absolute inset-0 bg-black/40" />
          </motion.div>
        </AnimatePresence>
      )}

      {/* Media stage */}
      <div
        {...swipe}
        // touch-action:pan-y lets vertical scroll/tap pass through to the
        // browser while we own horizontal (photo nav). Prevents iOS's edge
        // back-swipe from hijacking mid-drag.
        style={{ touchAction: 'pan-y' }}
        className="relative flex h-full w-full items-center justify-center p-4 sm:p-10"
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={item.key}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.02 }}
            transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
            className="flex max-h-full max-w-full items-center justify-center"
          >
            <Media
              fileKey={item.key}
              kind={item.kind}
              alt={item.displayName}
              priority
              playable={item.kind === 'video'}
              fit="contain"
              className="max-h-[82dvh] max-w-[92vw] rounded-2xl shadow-2xl ring-1 ring-white/10"
            />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Top bar */}
      <AnimatePresence>
        {showControls && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute inset-x-0 top-0 flex items-center justify-between p-4"
          >
            <span className="glass-dark rounded-full px-3 py-1 text-sm text-white">
              {index + 1} / {items.length}
            </span>
            <div className="flex items-center gap-2">
              {exif && (
                <ViewerButton label="Info" onClick={() => setShowInfo((s) => !s)}>
                  <IconInfoCircle className="h-5 w-5" />
                </ViewerButton>
              )}
              {onDownload && (
                <ViewerButton label="Download" onClick={() => onDownload(item)}>
                  <IconDownload className="h-5 w-5" />
                </ViewerButton>
              )}
              {onShare && (
                <ViewerButton label="Share" onClick={() => onShare(item)}>
                  <IconShare className="h-5 w-5" />
                </ViewerButton>
              )}
              <ViewerButton label="Close" onClick={onClose}>
                <IconX className="h-5 w-5" />
              </ViewerButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Nav arrows */}
      <AnimatePresence>
        {showControls && (
          <>
            {index > 0 && (
              <NavArrow side="left" onClick={prev}>
                <IconChevronLeft className="h-7 w-7" />
              </NavArrow>
            )}
            {index < items.length - 1 && (
              <NavArrow side="right" onClick={next}>
                <IconChevronRight className="h-7 w-7" />
              </NavArrow>
            )}
          </>
        )}
      </AnimatePresence>

      {/* Bottom controls: play/pause + thumbnail filmstrip */}
      <AnimatePresence>
        {showControls && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            className="absolute inset-x-0 bottom-0 flex flex-col items-center gap-3 bg-gradient-to-t from-black/70 to-transparent p-4 pt-10"
          >
            <ViewerButton
              label={slideshow.playing ? 'Pause slideshow' : 'Play slideshow'}
              onClick={slideshow.toggle}
            >
              {slideshow.playing ? (
                <IconPlayerPauseFilled className="h-5 w-5" />
              ) : (
                <IconPlayerPlayFilled className="h-5 w-5" />
              )}
            </ViewerButton>
            <Filmstrip items={items} index={index} onPick={goTo} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Info panel — bottom sheet on mobile, floating side panel on desktop. */}
      <AnimatePresence>
        {showInfo && exif && (
          <motion.aside
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 30 }}
            className="glass-dark absolute inset-x-4 bottom-4 rounded-2xl p-4 text-sm text-white sm:inset-x-auto sm:bottom-auto sm:right-4 sm:top-20 sm:w-64"
          >
            <h3 className="mb-2 font-semibold">{item.displayName}</h3>
            <dl className="space-y-1 text-xs">
              {exif.dateTaken && (
                <Row label="Taken" value={new Date(exif.dateTaken).toLocaleString()} />
              )}
              {exif.camera && <Row label="Camera" value={exif.camera} />}
              {exif.lens && <Row label="Lens" value={exif.lens} />}
              {exif.width && exif.height && (
                <Row label="Size" value={`${exif.width}×${exif.height}`} />
              )}
              {exif.gps && (
                <Row label="Location" value={`${exif.gps.lat.toFixed(4)}, ${exif.gps.lng.toFixed(4)}`} />
              )}
            </dl>
          </motion.aside>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function ViewerButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      title={label}
      className="grid h-11 w-11 place-items-center rounded-full glass-dark text-white transition duration-200 ease-standard hover:bg-black/60"
    >
      {children}
    </button>
  );
}

function NavArrow({
  side,
  onClick,
  children,
}: {
  side: 'left' | 'right';
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <motion.button
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClick}
      aria-label={side === 'left' ? 'Previous' : 'Next'}
      className={`absolute top-1/2 -translate-y-1/2 ${
        side === 'left' ? 'left-3' : 'right-3'
      } grid h-12 w-12 place-items-center rounded-full glass-dark text-white transition duration-200 ease-standard hover:bg-black/60`}
    >
      {children}
    </motion.button>
  );
}

function Filmstrip({
  items,
  index,
  onPick,
}: {
  items: MediaItem[];
  index: number;
  onPick: (i: number) => void;
}) {
  const stripRef = useRef<HTMLDivElement>(null);

  // Keep the active thumbnail centered as you navigate.
  useEffect(() => {
    const strip = stripRef.current;
    const active = strip?.querySelector<HTMLElement>(`[data-idx="${index}"]`);
    active?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, [index]);

  // Render a window around the active index to keep the DOM light.
  const start = Math.max(0, index - 20);
  const end = Math.min(items.length, index + 20);
  const windowed = items.slice(start, end);

  return (
    <div
      ref={stripRef}
      className="no-scrollbar flex max-w-full gap-1.5 overflow-x-auto px-2"
      role="listbox"
      aria-label="Photo filmstrip"
    >
      {windowed.map((it, j) => {
        const i = start + j;
        const active = i === index;
        return (
          <button
            key={it.key}
            data-idx={i}
            type="button"
            role="option"
            onClick={() => onPick(i)}
            aria-label={`Go to ${it.displayName}`}
            aria-selected={active}
            className={cn(
              'relative h-14 w-14 shrink-0 overflow-hidden rounded-lg transition duration-200 ease-standard',
              active
                ? 'ring-2 ring-accent scale-105'
                : 'opacity-50 ring-1 ring-white/20 hover:opacity-90',
            )}
          >
            <Media
              fileKey={it.thumbnailKey}
              kind={it.kind}
              alt={it.displayName}
              priority={active}
              className="h-full w-full"
            />
          </button>
        );
      })}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-muted">{label}</dt>
      <dd className="text-right">{value}</dd>
    </div>
  );
}
